import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice du nombre de jours entre deux dates";
const DESCRIPTION =
  "Trouvez rapidement le nombre exact de jours entre deux dates, avec equivalents en semaines, mois et annees. Pratique pour delais legaux, vacances, contrats et organisation.";
const KEYWORDS =
  "nombre de jours entre deux dates, calculatrice jours, calcul delai, jours restants, difference de dates";
const OG_URL = "https://do-calculate.com/calculator/fr/days-calculator/";

// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice du Nombre de Jours",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Calculatrices",
        item: "https://do-calculate.com/fr/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Calculatrice Jours",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Comment calculer le nombre de jours entre deux dates?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Entrez la date de début et la date de fin dans les champs prévus, puis cliquez sur le bouton Calculer pour obtenir la différence en plusieurs unités (jours, semaines, mois, années, heures).",
        },
      },
      {
        "@type": "Question",
        name: "À quoi sert une calculatrice de jours?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Elle est utilisée pour calculer la durée des projets, l'âge en détail, les jours restants pour les événements, les périodes de vacances, le calcul des contrats, la planification des événements, et toute opération mathématique liée aux dates.",
        },
      },
    ],
  },
];

const GREGORIAN_MONTHS = [
  "Janvier", "Février", "Mars", "Avril",
  "Mai", "Juin", "Juillet", "Août",
  "Septembre", "Octobre", "Novembre", "Décembre"
];

function DaysCalculator() {
  const [startDate, setStartDate] = useState({ day: "1", month: "1", year: "2024" });
  const [endDate, setEndDate] = useState({ day: new Date().getDate(), month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const calculateDays = () => {
    const start = new Date(parseInt(startDate.year), parseInt(startDate.month) - 1, parseInt(startDate.day));
    const end = new Date(parseInt(endDate.year), parseInt(endDate.month) - 1, parseInt(endDate.day));

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setError("Veuillez entrer des dates valides");
      setResults(null);
      return;
    }

    let first = start < end ? start : end;
    let second = start < end ? end : start;

    const diffTime = Math.abs(second - first);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30.436875);
    const diffYears = Math.floor(diffDays / 365.25);
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffSeconds = Math.floor(diffTime / 1000);

    setResults({
      days: diffDays,
      weeks: diffWeeks,
      months: diffMonths,
      years: diffYears,
      hours: diffHours,
      minutes: diffMinutes,
      seconds: diffSeconds,
      startDateStr: first.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
      endDateStr: second.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
    });
    setError(null);
  };

  const renderDateSelect = (date, setDate, label) => (
    <div className="mb-4">
      <label><strong>{label}:</strong></label>
      <div className="row">
        <div className="col-md-4">
          <select className="form-control" value={date.day} onChange={(e) => setDate({ ...date, day: e.target.value })}>
            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <select className="form-control" value={date.month} onChange={(e) => setDate({ ...date, month: e.target.value })}>
            {GREGORIAN_MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{i + 1} - {m}</option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <select className="form-control" value={date.year} onChange={(e) => setDate({ ...date, year: e.target.value })}>
            {Array.from({ length: 150 }, (_, i) => new Date().getFullYear() + 10 - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center p-3">
            <h1 style={{ fontSize: "20px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light p-3">
            <p>
              Cette calculatrice gratuite vous aide à calculer le nombre de jours, semaines, mois et années entre deux dates. Elle calcule également les heures, minutes et secondes pour une précision maximale.
            </p>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle p-3 bg-light rounded">
              <div className="p-2 mb-3 bg-success text-white">
                <strong>Entrez vos dates</strong>
              </div>

              {renderDateSelect(startDate, setStartDate, "Date de début")}
              {renderDateSelect(endDate, setEndDate, "Date de fin")}

              <div className="text-center mb-4">
                <button className="btn btn-success btn-lg px-5" onClick={calculateDays}>
                  Calculer le Nombre de Jours
                </button>
              </div>

              {error && <div className="alert alert-danger">{error}</div>}

              {results && (
                <div className="alert alert-success mt-4">
                  <h3>Résultats:</h3>
                  <div className="mb-3">
                    <strong style={{ fontSize: "24px", color: "#28a745" }}>
                      Différence: {results.days} jours
                    </strong>
                  </div>
                  <div className="mt-3">
                    <p><strong>Détails complets:</strong></p>
                    <ul>
                      <li><strong>Date de début:</strong> {results.startDateStr}</li>
                      <li><strong>Date de fin:</strong> {results.endDateStr}</li>
                      <li><strong>Jours totaux:</strong> {results.days}</li>
                      <li><strong>Semaines:</strong> {results.weeks}</li>
                      <li><strong>Mois:</strong> {results.months}</li>
                      <li><strong>Années:</strong> {results.years}</li>
                      <li><strong>Heures:</strong> {results.hours}</li>
                      <li><strong>Minutes:</strong> {results.minutes}</li>
                      <li><strong>Secondes:</strong> {results.seconds}</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
          <br />
          <SecondAds />
          <br />
          <MainCalculatorsList />
          <br />
          <RelatedLinks />
          <br />
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Calculatrice de jours</h3></div>
              <div className="info-section-body">
                <p>La calculatrice de jours permet de mesurer un ecart entre deux dates ou de verifier un delai a venir. C'est un gain de temps pour les dossiers administratifs, la gestion de contrat, les conges ou la planification d'un evenement.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Delais legaux frequents en France</h3></div>
              <div className="info-section-body">
                <ul>
                  <li><strong>Delai de retractation:</strong> 14 jours calendaires pour les achats en ligne</li>
                  <li><strong>Preavis locataire:</strong> 1 mois (meuble) ou 3 mois (non meuble)</li>
                  <li><strong>Delai de paiement:</strong> 30 jours entre professionnels (60 jours maximum)</li>
                  <li><strong>Prescription civile:</strong> 5 ans pour la plupart des creances</li>
                  <li><strong>Conge maternite:</strong> 16 semaines minimum (6 avant + 10 apres)</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Conseils pour utiliser efficacement la calculatrice</div>
              <div className="info-section-body">
                <ul>
                  <li>Vérifiez l'ordre des dates — la date de début doit précéder la date de fin</li>
                  <li>La calculatrice tient compte automatiquement des années bissextiles et des mois de longueurs différentes</li>
                  <li>Sauvegardez le résultat (copie d'écran) pour les dossiers administratifs ou contractuels</li>
                  <li>Pour les délais juridiques, précisez si le jour de départ est inclus ou non</li>
                </ul>
                <p><strong>Usages professionnels :</strong></p>
                <ul>
                  <li><strong>Projets :</strong> calcul précis de la durée d'une mission ou d'une phase</li>
                  <li><strong>Contrats :</strong> vérification de la durée totale d'un engagement</li>
                  <li><strong>Facturation :</strong> périodes de paiement, délais de règlement</li>
                  <li><strong>Planification :</strong> construction de rétro-plannings réalistes</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Faits intéressants sur les calendriers et les jours</div>
              <div className="info-section-body">
                <ul>
                  <li>Une année bissextile ajoute 1 jour (29 février) pour compenser les 5 h 48 min 45 s excédentaires par an</li>
                  <li>Le calendrier grégorien a été introduit par le pape Grégoire XIII en 1582 pour corriger les dérives du calendrier julien</li>
                  <li>100 ans = exactement <strong>36 524 jours</strong> (en comptant 24 années bissextiles sur 100)</li>
                  <li>Une vie humaine de 80 ans représente environ <strong>29 200 jours</strong></li>
                  <li>La grossesse dure 280 jours (40 semaines)</li>
                  <li>12 années d'école primaire et secondaire représentent environ <strong>2 160 jours</strong> de classe</li>
                  <li>Février est le seul mois à avoir 28 ou 29 jours — vestige de la réforme du calendrier romain</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Questions fréquentes</div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "16px" }}><strong>Comment la calculatrice compte-t-elle les jours ?</strong></h3>
                <p>Elle soustrait la date de début de la date de fin en tenant compte des longueurs de mois variables et des années bissextiles, pour un résultat toujours exact.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Les années bissextiles sont-elles prises en compte ?</strong></h3>
                <p>Oui, les années bissextiles (divisibles par 4, sauf les centenaires non divisibles par 400) sont automatiquement incluses dans le calcul.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Puis-je calculer des jours dans le futur ?</strong></h3>
                <p>Oui, entrez n'importe quelle date future comme date de fin pour connaître le nombre de jours jusqu'à cette échéance.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Quelle est la différence entre jours calendaires et jours ouvrés ?</strong></h3>
                <p>Les jours calendaires comptent TOUS les jours y compris les weekends et jours fériés. Les jours ouvrés excluent samedis, dimanches et jours fériés. Pour un contrat, précisez toujours le type utilisé.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>La calculatrice fonctionne-t-elle hors connexion ?</strong></h3>
                <p>Oui, l'outil fonctionne hors connexion après le premier chargement de la page, idéal pour une utilisation en déplacement.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DaysCalculator;
